// データ出力ビュー（中身/TMS）。運行実績×収支の各集計と元データを、月を選んでCSV(UTF-8 BOM/Excel対応)で書き出す。
// 集計は operations.js と同じ computeOperations を使うため、画面の数字とCSVは一致する。
import { computeOperations } from '../data/analytics/operations.js'
import { COLLECTIONS, listDocs } from '../data/collections.js'
import { supabaseReady } from '../supabase.js'
import sampleOps from './sample.ops.json'
import { allowSample, DEMO_TAG, noDataHtml } from '../data/demoData.js'

const pad = (n) => String(n).padStart(2, '0')
const monthOf = (o, kd) => o.month || (o[kd] ? '2026-' + pad(o[kd][0]) : (o.date ? String(o.date).slice(0, 7) : ''))
const num = (n) => (n == null || n === '' ? '' : Math.round(Number(n)))

const X = { el: null, ctx: {}, month: null, months: [], trips: [], tachos: [], names: {}, vehRates: {}, vehClass: {}, online: false, fetchError: false, isSample: false }

function styleOnce() {
  if (document.getElementById('dx-style')) return
  const s = document.createElement('style'); s.id = 'dx-style'
  s.textContent = `
  .dx{display:flex;flex-direction:column;gap:14px}
  .dx-bar{display:flex;align-items:center;gap:12px;flex-wrap:wrap}
  .dx-bar select{font-family:inherit;font-size:13px;padding:6px 10px;border:1px solid var(--e1);border-radius:8px;background:var(--card);color:var(--text)}
  .dx-hint{font-size:12px;color:var(--muted)}
  .dx-demo{font-size:11px;color:var(--signal);background:transparent;border:1px solid var(--accent);border-radius:999px;padding:3px 10px}
  .dx-all{margin-left:auto;font-size:12.5px;font-weight:700;color:var(--bg);background:var(--pos);border:0;border-radius:8px;padding:8px 14px;cursor:pointer}
  .dx-all:hover{background:var(--pos)}
  .dx-grid{display:grid;grid-template-columns:repeat(auto-fill,minmax(255px,1fr));gap:12px}
  .dx-card{border:1px solid var(--e1);border-radius:12px;background:var(--card);padding:13px 15px;display:flex;flex-direction:column;gap:7px}
  .dx-card h3{margin:0;font-size:14px;color:var(--text)}
  .dx-card .d{font-size:11.5px;color:var(--text);line-height:1.5;flex:1}
  .dx-card .r{font-size:12px;color:var(--text);font-variant-numeric:tabular-nums}
  .dx-btn{align-self:flex-start;font-size:12.5px;font-weight:700;color:var(--text);background:var(--head);border:1px solid var(--e1);border-radius:8px;padding:7px 13px;cursor:pointer}
  .dx-btn:hover{background:transparent}
  .dx-btn[disabled]{opacity:.45;cursor:default}
  .dx-note{font-size:11px;color:var(--muted);line-height:1.6}
  `
  document.head.appendChild(s)
}

// CSV生成（RFC4180風）。読み込みは download() 側で UTF-8 BOM を付けて Excel 対応。
function toCSV(headers, rows) {
  const esc = (v) => { const s = (v == null ? '' : String(v)); return /[",\n\r]/.test(s) ? '"' + s.replace(/"/g, '""') + '"' : s }
  return [headers.map(esc).join(',')].concat(rows.map((r) => r.map(esc).join(','))).join('\r\n')
}
function download(name, csv) {
  const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8;' }) // ﻿=BOM（ExcelでUTF-8日本語を正しく表示）
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a'); a.href = url; a.download = name
  document.body.appendChild(a); a.click(); a.remove()
  setTimeout(() => URL.revokeObjectURL(url), 1500)
}

// データセット定義：title, desc, rows, cols([見出し, 取り出し関数])。operations.js の各表と列を揃える。
function datasets(r, trips) {
  const kindLabel = (k) => k === 'own' ? '自社便' : (k === 'charter' ? '傭車' : (k || ''))
  return [
    { id: 'meisai', title: '便明細', desc: '1便ごと（発着・運賃・原価・粗利・収支）', rows: r.meisai, cols: [
      ['日付', (m) => m.date], ['区分', (m) => kindLabel(m.kind)], ['得意先', (m) => m.customer], ['乗務員/協力', (m) => m.who],
      ['発', (m) => m.from], ['着', (m) => m.to], ['車種', (m) => m.vType], ['運賃', (m) => num(m.fare)], ['人件費', (m) => num(m.jinken)],
      ['燃料', (m) => num(m.fuel)], ['傭車料', (m) => num(m.charterCost)], ['粗利益', (m) => num(m.arari)], ['車両経費', (m) => num(m.carCost)],
      ['収支', (m) => num(m.shushi)], ['収支率%', (m) => m.smargin], ['突合', (m) => m.matched ? '実測' : '推定']] },
    { id: 'custs', title: '客先別 収支', desc: '得意先ごとの売上・粗利', rows: r.custs, cols: [
      ['客先', (c) => c.name], ['総売上', (c) => num(c.uri)], ['自社便', (c) => num(c.ji)], ['実走行km', (c) => num(c.km)],
      ['実燃料', (c) => num(c.fuel)], ['傭車取り分', (c) => num(c.yo_arari)], ['粗利益', (c) => num(c.contrib)]] },
    { id: 'drivers', title: '乗務員別 収支', desc: '乗務員ごとの売上・人件費・収支', rows: r.drivers, cols: [
      ['乗務員', (d) => d.name], ['運行', (d) => d.n], ['稼働日', (d) => d.days], ['自社便売上', (d) => num(d.ji)], ['実走行km', (d) => num(d.km)],
      ['人件費', (d) => num(d.jinken)], ['車両経費', (d) => num(d.carCost)], ['実燃料', (d) => num(d.fuel)], ['粗利益', (d) => num(d.contrib)], ['収支', (d) => num(d.shushi)]] },
    { id: 'staff', title: '配車担当別 収支', desc: '配車担当ごと（自社便＋傭車）', rows: r.staffAgg, cols: [
      ['配車担当', (s) => s.staff], ['運行', (s) => s.n], ['自社便売上', (s) => num(s.uri)], ['傭車粗利', (s) => num(s.yoArari)],
      ['人件費', (s) => num(s.jinken)], ['実燃料', (s) => num(s.fuel)], ['粗利益', (s) => num(s.arari)], ['車両経費', (s) => num(s.carCost)], ['収支', (s) => num(s.shushi)]] },
    { id: 'driverDays', title: '日別乗務員 収支', desc: '乗務員×運行日', rows: r.driverDays, cols: [
      ['日付', (d) => d.date], ['乗務員', (d) => d.driver], ['車種', (d) => d.cls], ['走行km', (d) => num(d.km)], ['売上', (d) => num(d.uri)],
      ['人件費', (d) => num(d.jinken)], ['車両経費', (d) => num(d.carCost)], ['燃料', (d) => num(d.fuel)], ['収支', (d) => num(d.shushi)], ['収支率%', (d) => d.smargin]] },
    { id: 'vehicles', title: '車両別 採算', desc: '車番ごと（デジタコ突合分）', rows: r.vehicles, cols: [
      ['車番', (v) => v.name], ['稼働', (v) => v.days], ['走行km', (v) => num(v.km)], ['実車率%', (v) => (v.jissharitsu * 100).toFixed(0)],
      ['実燃費kmL', (v) => v.kmL], ['売上', (v) => num(v.uri)], ['粗利益', (v) => num(v.contrib)]] },
    { id: 'partners', title: '利用運送 収支', desc: '協力会社ごと（傭車）', rows: r.partners, cols: [
      ['協力会社', (p) => p.name], ['運行', (p) => p.n], ['請求(運賃)', (p) => num(p.uri)], ['支払(傭車)', (p) => num(p.pay)], ['粗利', (p) => num(p.arari)], ['粗利率%', (p) => p.rate]] },
    { id: 'custDrivers', title: '得意先×乗務員', desc: '得意先ごとの乗務員内訳', rows: r.custDrivers, cols: [
      ['得意先', (x) => x.customer], ['乗務員', (x) => x.driver], ['運行', (x) => x.n], ['自社便売上', (x) => num(x.uri)],
      ['人件費', (x) => num(x.jinken)], ['実燃料', (x) => num(x.fuel)], ['車両経費', (x) => num(x.carCost)], ['収支', (x) => num(x.shushi)]] },
    { id: 'trips', title: '全便（元データ）', desc: 'その月の生トリップ（バックアップ用）', rows: trips, cols: [
      ['運行日', (t) => t.date || (Array.isArray(t.dm) ? '2026-' + pad(t.dm[0]) + '-' + pad(t.dm[1]) : '')], ['区分', (t) => kindLabel(t.kind)],
      ['得意先', (t) => t.customerFormal || t.customerName || ''], ['乗務員CD', (t) => t.driverCode || ''], ['車番', (t) => t.carNo || t.vehicleNo || ''],
      ['車種', (t) => t.vehicleType || t.vehicleClass || ''], ['発', (t) => t.fromPlace || t.origin || ''], ['着', (t) => t.toPlace || t.dest || ''],
      ['運賃', (t) => num(t.fare)], ['傭車原価', (t) => num(t.charterCost)], ['協力会社', (t) => t.partnerFormal || t.partnerName || ''], ['配車担当', (t) => t.staff || '']] },
  ]
}

function buildCSV(ds) {
  const headers = ds.cols.map((c) => c[0])
  const rows = (ds.rows || []).map((row) => ds.cols.map((c) => { const v = c[1](row); return v == null ? '' : v }))
  return toCSV(headers, rows)
}

export async function renderDataExport(el, ctx = {}) {
  styleOnce()
  X.el = el
  X.ctx = ctx
  X.online = !ctx.demo && supabaseReady
  X.fetchError = false
  el.innerHTML = '<div class="dx-note" style="padding:16px">読み込み中…</div>'
  let trips = sampleOps.trips, tachos = sampleOps.tachographs
  if (X.online) {
    try {
      const [tr, ta, dr, vh] = await Promise.all([listDocs(COLLECTIONS.trips), listDocs(COLLECTIONS.tachographs), listDocs(COLLECTIONS.drivers), listDocs(COLLECTIONS.vehicles)])
      if (tr && tr.length) {
        trips = tr; tachos = ta || []; X.names = {}; X.vehRates = {}; X.vehClass = {}
        for (const d of (dr || [])) { const k = String(d.code || d.id || '').replace(/^0+(?=\d)/, ''); if (k) X.names[k] = d.name || '' }
        for (const m of (vh || [])) { const id = m.id; if (id == null) continue; if (m.dailyCost != null && m.dailyCost !== '') X.vehRates[id] = Number(m.dailyCost); if (m.klass) X.vehClass[id] = m.klass }
      } else if (!allowSample(X.online)) {
        // 実データ0件。サンプルへ落ちてよいかは共通判定 allowSample（設定 settings/display）に委ねる
        trips = []; tachos = []
      }
    } catch { X.online = false; X.fetchError = true }
  }
  // サインイン済みの実fetch失敗はサンプルへフォールバックせず is-error を主表示にする
  // （CSV出力元がサンプルにすり替わるのを防ぐ。サンプルは明示的なデモ状況のときだけ。2026-08-27 負債解消）
  if (X.fetchError) { X.trips = []; X.tachos = []; X.isSample = false; paintFetchError(); return }
  X.isSample = trips === sampleOps.trips
  // 実データ0件＋デモ表示OFF＝正直に空を出す（架空の実績をCSVの出力元にしない。2026-08-27 案C）
  if (!X.isSample && !trips.length) { X.trips = []; X.tachos = []; paintNoData(); return }
  X.trips = trips; X.tachos = tachos
  const ms = new Set(trips.map((t) => monthOf(t, 'dm')).filter(Boolean))
  X.months = [...ms].sort()
  X.month = X.months[X.months.length - 1] || null
  paint(ctx)
}

// 実fetch失敗（デモ意図とは別）の主表示。サンプルデータは描画しない
function paintFetchError() {
  X.el.innerHTML = '<section class="dx"><div class="ct-state is-error"><b class="ct-state__t">データを読み込めませんでした</b><span>保存済みの実データを取得できませんでした。サンプルをCSVに出力しないため、この画面は表示していません。接続を確認して再試行してください</span><button type="button" class="btn" id="dx-retry">再試行</button></div></section>'
  const retry = X.el.querySelector('#dx-retry'); if (retry) retry.addEventListener('click', () => { renderDataExport(X.el, X.ctx) })
}

// 実データ0件＋デモデータ表示OFF（既定）の主表示。サンプルは描かない＝CSVの出力元にもしない
function paintNoData() {
  X.el.innerHTML = `<section class="dx">${noDataHtml('データ出力')}</section>`
}

function paint(ctx) {
  const el = X.el
  const trips = X.trips.filter((t) => monthOf(t, 'dm') === X.month)
  const tachos = X.tachos.filter((t) => monthOf(t, 'sd') === X.month)
  const r = computeOperations(trips, tachos, { names: X.names, vehRates: X.vehRates, vehClass: X.vehClass })
  const ds = datasets(r, trips)
  const opts = X.months.map((m) => `<option value="${m}" ${m === X.month ? 'selected' : ''}>${m.replace('-', '年')}月</option>`).join('')
  const cards = ds.map((d) => {
    const n = (d.rows || []).length
    return `<div class="dx-card">
      <h3>${d.title}</h3>
      <div class="d">${d.desc}</div>
      <div class="r">${n.toLocaleString('ja-JP')} 行</div>
      <button class="dx-btn" data-ds="${d.id}" ${n ? '' : 'disabled'}>CSVで出力</button>
    </div>`
  }).join('')
  el.innerHTML = `
    <section class="dx">
      <div class="dx-bar">
        <select id="dx-month">${opts}</select>
        <span class="dx-hint">選んだ月のデータをCSV（UTF-8 BOM＝Excelでそのまま開けます）で書き出します。</span>
        <button class="dx-all" id="dx-all">全部まとめて出力（1ファイル）</button>
        ${X.isSample ? `<span class="dx-demo">${DEMO_TAG}</span>` : ''}
      </div>
      <div class="dx-grid">${cards}</div>
      <div class="dx-note">ファイル名は「VEIL_項目_${X.month || ''}.csv」。Excelで開くと日本語も文字化けしません。ネイティブの .xlsx（複数シートを1ファイル）が必要なら対応します。</div>
    </section>`
  el.querySelector('#dx-month').addEventListener('change', (e) => { X.month = e.target.value; paint(ctx) })
  el.querySelectorAll('[data-ds]').forEach((b) => b.addEventListener('click', () => {
    const d = ds.find((x) => x.id === b.dataset.ds); if (!d || !(d.rows || []).length) return
    download(`VEIL_${d.title}_${X.month}.csv`, buildCSV(d))
  }))
  el.querySelector('#dx-all').addEventListener('click', () => {
    const parts = ds.filter((d) => (d.rows || []).length).map((d) => `■ ${d.title}\r\n` + buildCSV(d))
    if (parts.length) download(`VEIL_運行実績まとめ_${X.month}.csv`, parts.join('\r\n\r\n'))
  })
}
